import { forwardRef } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Operator-grade checkbox.
 *
 * Replaces the bright-white native checkbox that ships out of the
 * box on every platform. The native control is rendered invisible
 * (`appearance-none`) and we layer a custom box + check glyph on
 * top so the visual style matches the dark operator theme: dim
 * border + base background when off, brand fill + white check when
 * on, dash glyph when indeterminate.
 *
 * Important properties:
 *   - Stays a real `<input type="checkbox">` so screen-reader and
 *     keyboard semantics (Space toggles, Tab traverses) come for
 *     free.
 *   - Supports `indeterminate` via the `indeterminate` prop —
 *     forwarded to the DOM element via the imperative property,
 *     which is the only way HTML exposes the tri-state.
 *   - Forwards refs so DataTable's "select-page" header checkbox
 *     can still reach into the DOM if it wants to.
 */

export type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  /** Tri-state. Set the indeterminate flag on the DOM element on
   *  every render — there's no HTML attribute equivalent. */
  indeterminate?: boolean;
  size?: "sm" | "md";
};

const SIZE_CLS = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
} as const;

const ICON_SIZE = {
  sm: 10,
  md: 11,
} as const;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ className, indeterminate, size = "md", checked, disabled, ...rest }, refIn) {
    return (
      <span
        className={cn(
          // Wrapper is the visible box. The native input sits on
          // top with `inset-0` + `opacity-0` so clicks land on it
          // (preserving accessibility); we paint state via classes
          // on this span using `peer-*` selectors.
          "relative inline-flex items-center justify-center rounded-[5px] border transition-colors",
          SIZE_CLS[size],
          // Off state — neutral border + base bg.
          "border-line bg-bg-base/80",
          // Hover lifts the border so an operator can see the
          // affordance before clicking.
          !disabled && "hover:border-line-strong",
          // On (checked or indeterminate) — brand fill, brand border.
          (checked || indeterminate) &&
            "border-brand-500 bg-brand-500 hover:border-brand-400",
          // Focus ring on the native input is mirrored on the box
          // via has-[:focus-visible].
          "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/40 has-[:focus-visible]:ring-offset-1 has-[:focus-visible]:ring-offset-bg-base",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
        aria-hidden={false}
      >
        <input
          ref={(el) => {
            // Forward the ref AND apply the indeterminate flag —
            // both happen here so the imperative DOM update can't
            // be skipped on a render where the consumer didn't
            // re-set indeterminate.
            if (el) el.indeterminate = !!indeterminate;
            if (typeof refIn === "function") refIn(el);
            else if (refIn) (refIn as React.MutableRefObject<HTMLInputElement | null>).current = el;
          }}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          {...rest}
        />
        {indeterminate ? (
          <Minus
            size={ICON_SIZE[size]}
            strokeWidth={3}
            className="text-white pointer-events-none"
            aria-hidden
          />
        ) : checked ? (
          <Check
            size={ICON_SIZE[size]}
            strokeWidth={3}
            className="text-white pointer-events-none"
            aria-hidden
          />
        ) : null}
      </span>
    );
  },
);
